import {
  Button,
  CloseButton,
  Flex,
  Grid,
  Heading,
  IconAddLine,
  IconButton,
  IconTrashLine,
  List,
  Modal,
  Spinner,
  TextInput,
} from "@instructure/ui";
import { t } from "i18next";
import React, { useState } from "react";
import { GoalsService } from "../../services/CyclesService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";

export const CycleGoalEditModal = ({
  cycleGoalPanelOpen,
  cycleGoal,
  onCloseHandler,
}) => {
  const showNotification = useNotification();
  const [newCycleGoal, setNewCycleGoal] = useState({
    ...cycleGoal,
    objectives: [...cycleGoal.objectives],
  });
  const [loading, isLoading] = useState(false);

  const handleClose = () => {
    onCloseHandler(null);
  };

  const addGoalObjective = () => {
    newCycleGoal.objectives.push({ description: "" });
    setNewCycleGoal({
      ...newCycleGoal,
      objectives: [...newCycleGoal.objectives],
    });
  };

  const removeGoalObjective = (objectiveToRemove) => {
    newCycleGoal.objectives = newCycleGoal.objectives.filter(
      (objective) => objective.description !== objectiveToRemove.description
    );
    setNewCycleGoal({ ...newCycleGoal });
    cycleGoal.objectives = newCycleGoal.objectives;
  };

  const handleSubmit = () => {
    isLoading(true);
    if (newCycleGoal.objectives) {
      const obj_len = newCycleGoal.objectives.length - 1;
      for (let i = obj_len; i >= 0; i--) {
        if (newCycleGoal.objectives[i].description === "") {
          newCycleGoal.objectives.pop(i);
        }
      }
    }
    const promise = newCycleGoal.id
      ? GoalsService.updateGoal(newCycleGoal)
      : GoalsService.createGoal(newCycleGoal);
    promise
      .then((newGoal) => {
        isLoading(false);
        handleClose();
        onCloseHandler(newGoal);
        showNotification(t("notifications.cycle.createGoalSuccess"));
      })
      .catch((e) => {
        showNotification(
          `${t("notifications.cycle.createGoalError")} ${e}`,
          ERROR
        );
      });
  };

  const getModalHeader = () => (
    <Modal.Header>
      <Grid hAlign="space-between">
        <Grid.Row>
          <Grid.Col>
            <Heading level="h2" as="h1">
              {t("cyclesAndGoals.cycleGoal.newCycleGoal")}
            </Heading>
          </Grid.Col>
          <Grid.Col width="auto">
            <CloseButton
              size="small"
              screenReaderLabel={t("global.close")}
              onClick={handleClose}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </Modal.Header>
  );

  const getModalBody = () => {
    return (
      <>
        <Grid hAlign="end" vAlign="middle">
          <Grid.Row>
            <Grid.Col width="auto">
              <Button
                color="primary"
                onClick={addGoalObjective}
                renderIcon={IconAddLine}
                key={"cycle-add-button"}
              >
                {t("cyclesAndGoals.cycleGoal.addGoalObjectiveButton")}
              </Button>
            </Grid.Col>
          </Grid.Row>
        </Grid>
        <Grid colSpacing="small" rowSpacing="small" vAlign="middle">
          <Grid.Row key={"edited-goal-row"}>
            <Grid.Col width={12}>
              <TextInput
                value={newCycleGoal.description ? newCycleGoal.description : ""}
                interaction={"enabled"}
                id={"goal-name"}
                renderLabel={t("cyclesAndGoals.cycleGoal.cycleGoalDescription")}
                onChange={(e) => {
                  newCycleGoal.description = e.currentTarget.value;
                  setNewCycleGoal({
                    ...newCycleGoal,
                    objectives: [...newCycleGoal.objectives],
                  });
                }}
              />
            </Grid.Col>
          </Grid.Row>
        </Grid>
        {newCycleGoal.objectives && newCycleGoal.objectives.length > 0 ? (
          <List isUnstyled>
            {newCycleGoal.objectives.map((objective, index) => {
              return (
                <List.Item key={index}>
                  <Flex justifyItems="space-between" alignItems="end">
                    <Flex.Item shouldShrink shouldGrow padding="0 small 0 0">
                      <div style={{ paddingTop: 15 }}>
                        <Grid>
                          <Grid.Row key={"edited-goal-row"}>
                            <Grid.Col width={12}>
                              <TextInput
                                value={objective.description}
                                id={"objective-name"}
                                renderLabel={t(
                                  "cyclesAndGoals.cycleGoal.cycleObjectiveDescription"
                                )}
                                onChange={(e) => {
                                  newCycleGoal.objectives[index].description =
                                    e.currentTarget.value;
                                  setNewCycleGoal({
                                    ...newCycleGoal,
                                    objectives: [...newCycleGoal.objectives],
                                  });
                                }}
                              />
                            </Grid.Col>
                          </Grid.Row>
                        </Grid>
                      </div>
                    </Flex.Item>
                    {index !== 0 && (
                      <Flex.Item>
                        <IconButton
                          shape="rectangle"
                          screenReaderLabel={t(
                            "cyclesAndGoals.cycleType.deleteTagScreenReaderLabel"
                          )}
                          onClick={() => {
                            removeGoalObjective(objective);
                          }}
                        >
                          <IconTrashLine />
                        </IconButton>
                      </Flex.Item>
                    )}
                  </Flex>
                </List.Item>
              );
            })}
          </List>
        ) : (
          <List isUnstyled>
            <List.Item key={0}>
              <Flex justifyItems="space-between" alignItems="center">
                <Flex.Item shouldShrink shouldGrow>
                  <div style={{ paddingTop: 15 }}>
                    <Grid>
                      <Grid.Row key={"edited-goal-row"}>
                        <Grid.Col width={12}>
                          <TextInput
                            value={""}
                            id={"objective-name"}
                            renderLabel={t(
                              "cyclesAndGoals.cycleGoal.cycleObjectiveDescription"
                            )}
                            onChange={(e) => {
                              newCycleGoal.objectives[0].description =
                                e.currentTarget.value;
                              setNewCycleGoal({
                                ...newCycleGoal,
                                objectives: [...newCycleGoal.objectives],
                              });
                            }}
                          />
                        </Grid.Col>
                      </Grid.Row>
                    </Grid>
                  </div>
                </Flex.Item>
              </Flex>
            </List.Item>
          </List>
        )}
      </>
    );
  };

  const getModalFooter = () => (
    <Modal.Footer>
      <Button margin="0 small 0 0" onClick={handleClose}>
        {t("global.cancel")}
      </Button>

      <Button
        color="primary"
        type="submit"
        onClick={handleSubmit}
        disabled={loading}
      >
        {loading ? (
          <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
        ) : (
          t("global.submit")
        )}
      </Button>
    </Modal.Footer>
  );

  return (
    <Modal
      key={"cycle-goal-edit-modal"}
      open={cycleGoalPanelOpen}
      onDismiss={handleClose}
      size="large"
      label={t("cyclesAndGoals.cycleGoal.configure")}
      shouldCloseOnDocumentClick
    >
      {getModalHeader()}
      <Modal.Body>{getModalBody()}</Modal.Body>
      {getModalFooter()}
    </Modal>
  );
};
